import { useNavigate } from "react-router-dom";
import { Train, Wrench, ArrowRight, ShieldCheck, BookOpen, Lock, Siren, FileText, ClipboardCopy } from "lucide-react";
import PnjLogo from "@/components/PnjLogo";

/**
 * HomeMenu — landing screen dengan dua kartu pilihan aplikasi.
 *   1. Shunting Alerts (aktif, mengarah ke passcode gate → dashboard)
 *   2. Failure Guidance List (coming soon)
 *
 * Design mengikuti bahasa visual Shunting Alerts: dark canvas, gradient
 * cyan → green, monospace + serif blend, radial glow. Kartu memiliki
 * hover interaksi (lift + border glow) untuk feel premium.
 */
export default function HomeMenu({ onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0c1b2a] text-white relative overflow-hidden">
      {/* backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-30 home-grid" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(14,165,233,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.12),transparent_60%)]" />
      </div>

      {/* top bar */}
      <header className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 pt-8">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#034694] flex items-center justify-center">
            <Train className="h-5 w-5" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400">
              CRLS · Console Suite
            </div>
            <div className="text-sm font-display font-semibold">
              MRT Jakarta Operations
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4">
          {onLogout && (
            <button
              onClick={onLogout}
              data-testid="home-lock-btn"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-white transition-colors px-3 py-2 rounded-md border border-[#243a52] hover:border-[#0ea5e9]/60"
              title="Kunci console (logout passcode)"
            >
              <Lock className="h-3.5 w-3.5" />
              Kunci
            </button>
          )}
          <PnjLogo size={36} />
        </div>
      </header>

      {/* hero */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-14 pb-16">
        <div className="mb-3 text-xs uppercase tracking-[0.45em] text-[#289643]">
          OCC Dashboard
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
          Selamat Datang di{" "}
          <span className="text-[#7dd3fc]">OCC dashboard</span>
        </h1>
        <p className="mt-4 text-slate-300 max-w-2xl">
          Silahkan pilih Menu dibawah ini untuk membantu Operational anda.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {/* Card 1 — Shunting Alerts */}
          <button
            onClick={() => navigate("/shunting-alerts")}
            data-testid="menu-shunting-alerts"
            className="group relative text-left rounded-2xl p-7 bg-gradient-to-br from-[#0d243a] to-[#062036] border border-[#243a52] hover:border-[#0ea5e9]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(14,165,233,0.55)] focus:outline-none focus:ring-2 focus:ring-[#0ea5e9]/60"
          >
            <div className="flex items-start justify-between">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#22c55e] flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Train className="h-7 w-7 text-[#0c1b2a]" strokeWidth={2.4} />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] px-2 py-1 rounded bg-[#289643]/20 text-[#22c55e] border border-[#22c55e]/30">
                Aktif
              </span>
            </div>

            <h2 className="mt-6 font-display text-2xl font-bold">
              Shunting Alerts
            </h2>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Startup & Departure Alert System — pengingat T-30 menit dan alarm
              tepat pada jam keberangkatan trainset sesuai shunting plan MRT
              Jakarta.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <span className="px-2 py-1 rounded bg-[#122032]">PDF Ingest</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Auto T-30</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Push Alert</span>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <ShieldCheck className="h-4 w-4 text-[#289643]" />
                Passcode required
              </div>
              <div className="flex items-center gap-2 text-[#7dd3fc] font-semibold group-hover:gap-3 transition-all">
                Buka
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>

          {/* Card 2 — Pelaporan Gangguan Operasional */}
          <button
            onClick={() => navigate("/incident-report")}
            data-testid="menu-incident-report"
            className="group relative text-left rounded-2xl p-7 bg-gradient-to-br from-[#0d243a] to-[#062036] border border-[#243a52] hover:border-[#a855f7]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(168,85,247,0.55)] focus:outline-none focus:ring-2 focus:ring-[#a855f7]/60"
          >
            <div className="flex items-start justify-between">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#a855f7] to-[#0ea5e9] flex items-center justify-center shadow-lg shadow-purple-500/30">
                <FileText className="h-7 w-7 text-[#0c1b2a]" strokeWidth={2.4} />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] px-2 py-1 rounded bg-[#289643]/20 text-[#22c55e] border border-[#22c55e]/30">
                Aktif
              </span>
            </div>

            <h2 className="mt-6 font-display text-2xl font-bold leading-snug">
              Pelaporan Gangguan Operasional
            </h2>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Susun laporan keterlambatan dengan cepat — form terstruktur, kode
              singkat penanganan, autocomplete PIC, dan preview siap salin.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <span className="px-2 py-1 rounded bg-[#122032]">Template</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Kode Singkat</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Copy</span>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <ClipboardCopy className="h-4 w-4 text-[#c4b5fd]" />
                Auto-save isian terakhir
              </div>
              <div className="flex items-center gap-2 text-[#c4b5fd] font-semibold group-hover:gap-3 transition-all">
                Buka
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>

          {/* Card 3 — Failure Guidance List */}
          <button
            onClick={() => navigate("/failure-guidance")}
            data-testid="menu-failure-guidance"
            className="group relative text-left rounded-2xl p-7 bg-gradient-to-br from-[#0d243a] to-[#062036] border border-[#243a52] hover:border-[#f59e0b]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(245,158,11,0.55)] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/60"
          >
            <div className="flex items-start justify-between">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#f59e0b] to-[#dc2626] flex items-center justify-center shadow-lg shadow-orange-500/30">
                <Wrench className="h-7 w-7 text-[#0c1b2a]" strokeWidth={2.4} />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] px-2 py-1 rounded bg-[#289643]/20 text-[#22c55e] border border-[#22c55e]/30">
                Aktif
              </span>
            </div>

            <h2 className="mt-6 font-display text-2xl font-bold">
              Failure Guidance List
            </h2>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Panduan penanganan cepat untuk setiap kegagalan operasional
              trainset — Rollingstock, Power, dan Facility. Cari abbreviation,
              baca cause & handling langsung.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <span className="px-2 py-1 rounded bg-[#122032]">Rollingstock</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Power</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Facility</span>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <BookOpen className="h-4 w-4 text-[#fbbf24]" />
                316+ entries siap pakai
              </div>
              <div className="flex items-center gap-2 text-[#fbbf24] font-semibold group-hover:gap-3 transition-all">
                Buka
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>

          {/* Card 4 — Panduan Penanganan saat Gangguan dan Darurat */}
          <button
            onClick={() => navigate("/emergency-guidance")}
            data-testid="menu-emergency-guidance"
            className="group relative text-left rounded-2xl p-7 bg-gradient-to-br from-[#0d243a] to-[#062036] border border-[#243a52] hover:border-[#dc2626]/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(220,38,38,0.55)] focus:outline-none focus:ring-2 focus:ring-[#dc2626]/60"
          >
            <div className="flex items-start justify-between">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#dc2626] to-[#f97316] flex items-center justify-center shadow-lg shadow-red-500/30">
                <Siren className="h-7 w-7 text-[#0c1b2a]" strokeWidth={2.4} />
              </div>
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] px-2 py-1 rounded bg-[#289643]/20 text-[#22c55e] border border-[#22c55e]/30">
                Aktif
              </span>
            </div>

            <h2 className="mt-6 font-display text-2xl font-bold leading-snug">
              Panduan Penanganan saat Gangguan dan Darurat
            </h2>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Menu ini mencakup prosedur operasi kereta saat terjadi gangguan
              dan keadaan darurat.
            </p>

            <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <span className="px-2 py-1 rounded bg-[#122032]">Gangguan</span>
              <span className="px-2 py-1 rounded bg-[#122032]">Darurat</span>
              <span className="px-2 py-1 rounded bg-[#122032]">SOP Operasi</span>
            </div>

            <div className="mt-6 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <BookOpen className="h-4 w-4 text-[#fbbf24]" />
                SOP-ROP/TO-02 Rev.05
              </div>
              <div className="flex items-center gap-2 text-[#fbbf24] font-semibold group-hover:gap-3 transition-all">
                Buka
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>

        </div>

        <footer className="mt-16 pt-6 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500">
          <div>
            <span className="text-slate-400 font-display font-semibold">
              CRLS Console Suite
            </span>{" "}
            · MRT Jakarta Operations · v1.0
          </div>
          <PnjLogo size={32} />
        </footer>
      </main>

      <style>{`
        .home-grid {
          background-image:
            linear-gradient(rgba(14,165,233,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.06) 1px, transparent 1px);
          background-size: 42px 42px;
        }
        .stripes-wip {
          background-image: repeating-linear-gradient(
            45deg,
            #fbbf24 0px,
            #fbbf24 12px,
            transparent 12px,
            transparent 24px
          );
        }
      `}</style>
    </div>
  );
}
